import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIncidents } from 'context/IncidentsContext';
import { humanizeSupabaseError } from '../../../lib/supabaseErrors';
import { openPrintUrl } from '../../../lib/openPrint';

import StudentPhotoUpload from './StudentPhotoUpload';
import QuickStatsCard from './QuickStatsCard';
import RecentActivityTimeline from './RecentActivityTimeline';
import DocumentsList from './DocumentsList';
import CollaborationIndicator from './CollaborationIndicator';
import StudentProfileForm from './StudentProfileForm';
import StudentTeamPanel from './StudentTeamPanel';
import TabNavigation from './TabNavigation';
import BehaviorSupportPlan from './BehaviorSupportPlan';
import CollectDataButton from '../../../components/CollectDataButton';

// ---------------------------------------------------------------------------
// StudentProfileView — the single-student profile screen.
//
// Loads a *real* student from StudentsContext (never mock data). Persists
// every edit / photo change / archive back through updateStudent, which
// writes to Supabase in live mode and localStorage in demo mode.
//
// Derived stats: we pull real behavioral-incident counts from IncidentsContext
// so the "Behavioral Incidents" tile in Quick Stats reflects actual logged
// data, not a static number.
//
// IEP goals / documents lists: those data streams don't have their own
// context yet (see docs/USER_MANUAL.md and the roadmap in ADMIN_GUIDE.md).
// We render them with empty-state UI so the tabs still work and don't crash;
// hooking up real goal/document storage is a follow-up.
// ---------------------------------------------------------------------------

const disabilityLabel = (raw) => {
  if (!raw) return '';
  return String(raw).replace(/[-_]/g, ' ');
};

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const calcAge = (dob) => {
  const d = safeDate(dob);
  if (!d) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};

// Given the annual-review deadline, decide the color and short label for
// the header pill. Buckets: overdue (red), <=30d (amber), <=90d (blue),
// beyond that (subtle gray).
const iepReviewInfo = (dueRaw) => {
  const due = safeDate(dueRaw);
  if (!due) return null;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const days = Math.round((due - startOfToday) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return {
      tone: 'overdue',
      label: `Review overdue by ${Math.abs(days)}d`,
      classes:
        'bg-red-50 text-red-700 border-red-200',
    };
  }
  if (days === 0) {
    return {
      tone: 'today',
      label: 'Review due today',
      classes: 'bg-red-50 text-red-700 border-red-200',
    };
  }
  if (days <= 30) {
    return {
      tone: 'soon',
      label: `Review due in ${days}d`,
      classes: 'bg-amber-50 text-amber-800 border-amber-200',
    };
  }
  if (days <= 90) {
    return {
      tone: 'upcoming',
      label: `Review in ${days}d`,
      classes: 'bg-blue-50 text-blue-700 border-blue-200',
    };
  }
  return {
    tone: 'far',
    label: `Review ${due.toLocaleDateString()}`,
    classes: 'bg-muted text-muted-foreground border-border',
  };
};

const StudentProfileView = ({ student }) => {
  const navigate = useNavigate();
  const { updateStudent, removeStudent } = useStudents();
  const { incidents } = useIncidents();

  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isArchived = student.status === 'archived';

  const studentIncidents = useMemo(
    () =>
      (Array.isArray(incidents) ? incidents : []).filter(
        (i) => i.studentId === student.id,
      ),
    [incidents, student.id],
  );

  const derivedStats = useMemo(
    () => ({
      activeGoals: student.stats?.activeGoals ?? 0,
      completedGoals: student.stats?.completedGoals ?? 0,
      behavioralIncidents: studentIncidents.length,
      serviceHours: student.stats?.serviceHours ?? 0,
      overallProgress: student.stats?.overallProgress ?? 0,
    }),
    [student.stats, studentIncidents.length],
  );

  const enrichedStudent = useMemo(
    () => ({
      ...student,
      studentId: student.studentId || student.id,
      stats: derivedStats,
    }),
    [student, derivedStats],
  );

  // Placeholder collaborators — no realtime-presence context yet.
  const activeCollaborators = [];

  const handleSave = async (formData) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateStudent(student.id, formData);
      setIsEditing(false);
      setHasUnsavedChanges(false);
    } catch (err) {
      setSaveError(
        humanizeSupabaseError(err, 'Could not save changes. Please try again.'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setSaveError(null);
  };

  const handlePhotoUpdate = async (nextPhoto) => {
    setSaveError(null);
    try {
      await updateStudent(student.id, { photo: nextPhoto || null });
    } catch (err) {
      setSaveError(humanizeSupabaseError(err, 'Photo could not be saved.'));
    }
  };

  const handleDocumentUpload = () => {
    // Placeholder — real document storage lands in a follow-up PR.
  };

  const handleDocumentDelete = () => {
    // Placeholder — real document storage lands in a follow-up PR.
  };

  const handleExportData = () => {
    const blob = new Blob([JSON.stringify(student, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `student-${student.id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrintProfile = () => {
    // Open the clean, print-first summary route in a new tab so the
    // user's editing state on the profile stays intact. If the browser
    // silently blocks the popup, `openPrintUrl` falls back to a
    // same-tab navigation so the button never appears to do nothing.
    const url = `/print/student/${encodeURIComponent(student.id)}`;
    openPrintUrl(url);
  };

  const handleArchiveStudent = async () => {
    const confirmed = window.confirm(
      isArchived
        ? 'Restore this student to your active roster?'
        : 'Archive this student? They will be hidden from the active roster (you can restore later).',
    );
    if (!confirmed) return;
    try {
      await updateStudent(student.id, {
        status: isArchived ? 'active' : 'archived',
      });
      if (!isArchived) navigate('/student-profile-management');
    } catch (err) {
      setSaveError(
        humanizeSupabaseError(err, 'Could not update archive state.'),
      );
    }
  };

  const handleDeleteStudent = async () => {
    const confirmed = window.confirm(
      'Permanently delete this student and all their data? This cannot be undone.',
    );
    if (!confirmed) return;
    try {
      await removeStudent(student.id);
      navigate('/student-profile-management');
    } catch (err) {
      setSaveError(humanizeSupabaseError(err, 'Could not delete student.'));
    }
  };

  const displayName =
    `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
    'Unnamed student';
  const age = calcAge(student.dateOfBirth);
  const initials =
    `${(student.firstName?.[0] || '?').toUpperCase()}${(student.lastName?.[0] || '').toUpperCase()}`;
  const reviewInfo = iepReviewInfo(student.iepAnnualReviewDue);

  return (
    <>
      <CollaborationIndicator activeUsers={activeCollaborators} />
      <div className="min-h-full">
        {/* Page Header */}
        <div className="bg-surface border-b border-border px-4 sm:px-6 py-4 sm:py-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <Link
                to="/dashboard"
                className="hover:text-foreground inline-flex items-center gap-1"
              >
                <Icon name="LayoutDashboard" size={14} />
                Dashboard
              </Link>
              <Icon name="ChevronRight" size={12} />
              <Link
                to="/student-profile-management"
                className="hover:text-foreground"
              >
                Students
              </Link>
              <Icon name="ChevronRight" size={12} />
              <span className="text-foreground font-medium truncate max-w-[12rem]">
                {displayName}
              </span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-lg flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                      {displayName}
                    </h1>
                    {isArchived && (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                        Archived
                      </span>
                    )}
                    {reviewInfo && !isArchived && (
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${reviewInfo.classes}`}
                        title={
                          student.iepAnnualReviewDue
                            ? `IEP annual review: ${new Date(
                                student.iepAnnualReviewDue,
                              ).toLocaleDateString()}`
                            : undefined
                        }
                      >
                        <Icon name="CalendarClock" size={12} />
                        {reviewInfo.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <span className="font-mono text-xs">
                      {student.id}
                    </span>
                    {student.grade ? (
                      <>
                        <span>·</span>
                        <span>Grade {student.grade}</span>
                      </>
                    ) : null}
                    {age !== null && (
                      <>
                        <span>·</span>
                        <span>Age {age}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {!isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/student-profile-management')}
                      iconName="Users"
                      iconPosition="left"
                    >
                      Back to roster
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportData}
                      iconName="Download"
                      iconPosition="left"
                      className="hidden sm:inline-flex"
                    >
                      Export
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrintProfile}
                      iconName="Printer"
                      iconPosition="left"
                      className="hidden md:inline-flex"
                    >
                      Print
                    </Button>
                    {/* Collect Data (student pre-picked) is the highest-
                        value action from a student profile page. Shows up
                        as the primary CTA so a para looking at Marcus can
                        one-tap into logging his data. */}
                    <CollectDataButton
                      defaultStudentId={student.id}
                      label="Collect Data"
                      variant="default"
                      size="sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      iconName="Edit"
                      iconPosition="left"
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Editing</span>
                    <div className="w-2 h-2 bg-warning rounded-full animate-pulse" />
                  </div>
                )}
              </div>
            </div>

            {saveError && (
              <div className="mt-3 p-2 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-foreground whitespace-pre-line">
                <span className="font-medium">Save error:</span> {saveError}
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        {/* Main content */}
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {isEditing ? (
                    <StudentProfileForm
                      student={enrichedStudent}
                      onSave={handleSave}
                      onCancel={handleCancel}
                      isEditing={isEditing}
                      isSaving={isSaving}
                    />
                  ) : (
                    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-card-foreground">
                          Student profile
                        </h2>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                          iconName="Edit"
                          iconPosition="left"
                        >
                          Edit
                        </Button>
                      </div>

                      <div className="space-y-6">
                        <section>
                          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                            <Icon name="User" size={18} />
                            Basic information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Full name</span>
                              <p className="font-medium">{displayName}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Grade</span>
                              <p className="font-medium">
                                {student.grade ? `Grade ${student.grade}` : '—'}
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Date of birth</span>
                              <p className="font-medium">
                                {safeDate(student.dateOfBirth)
                                  ? safeDate(student.dateOfBirth).toLocaleDateString()
                                  : 'Not set'}
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Student ID</span>
                              <p className="font-medium font-mono">{student.id}</p>
                            </div>
                          </div>
                        </section>

                        <section className="pt-6 border-t border-border">
                          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                            <Icon name="CalendarClock" size={18} />
                            IEP timeline
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">
                                IEP start date
                              </span>
                              <p className="font-medium">
                                {safeDate(student.iepStartDate)
                                  ? safeDate(
                                      student.iepStartDate,
                                    ).toLocaleDateString()
                                  : 'Not set'}
                              </p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">
                                Annual review due
                              </span>
                              <p className="font-medium">
                                {safeDate(student.iepAnnualReviewDue)
                                  ? safeDate(
                                      student.iepAnnualReviewDue,
                                    ).toLocaleDateString()
                                  : 'Not set'}
                                {reviewInfo && (
                                  <span
                                    className={`ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${reviewInfo.classes}`}
                                  >
                                    {reviewInfo.label}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </section>

                        <section className="pt-6 border-t border-border">
                          <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                            <Icon name="Heart" size={18} />
                            Disability information
                          </h3>
                          <div className="space-y-3 text-sm">
                            <div>
                              <span className="text-muted-foreground">Primary disability</span>
                              <p className="font-medium capitalize">
                                {disabilityLabel(student.primaryDisability) || 'Not specified'}
                              </p>
                            </div>
                            {Array.isArray(student.secondaryDisabilities) &&
                              student.secondaryDisabilities.length > 0 && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Secondary disabilities
                                  </span>
                                  <p className="font-medium capitalize">
                                    {student.secondaryDisabilities
                                      .map((d) => disabilityLabel(d))
                                      .join(', ')}
                                  </p>
                                </div>
                              )}
                            {student.accommodations && (
                              <div>
                                <span className="text-muted-foreground">Accommodations</span>
                                <p className="font-medium whitespace-pre-line">
                                  {student.accommodations}
                                </p>
                              </div>
                            )}
                          </div>
                        </section>

                        {(student.communicationMethod ||
                          student.academicLevel ||
                          student.medicalInfo ||
                          student.behavioralNotes ||
                          student.transportationNeeds ||
                          student.dietaryRestrictions) && (
                          <section className="pt-6 border-t border-border">
                            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                              <Icon name="FileText" size={18} />
                              Support details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {student.communicationMethod && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Communication method
                                  </span>
                                  <p className="font-medium capitalize">
                                    {String(student.communicationMethod).replace(/-/g, ' ')}
                                  </p>
                                </div>
                              )}
                              {student.academicLevel && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Academic level
                                  </span>
                                  <p className="font-medium whitespace-pre-line">
                                    {student.academicLevel}
                                  </p>
                                </div>
                              )}
                              {student.medicalInfo && (
                                <div className="md:col-span-2">
                                  <span className="text-muted-foreground">
                                    Medical information
                                  </span>
                                  <p className="font-medium whitespace-pre-line">
                                    {student.medicalInfo}
                                  </p>
                                </div>
                              )}
                              {student.behavioralNotes && (
                                <div className="md:col-span-2">
                                  <span className="text-muted-foreground">
                                    Behavioral notes
                                  </span>
                                  <p className="font-medium whitespace-pre-line">
                                    {student.behavioralNotes}
                                  </p>
                                </div>
                              )}
                              {student.transportationNeeds && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Transportation needs
                                  </span>
                                  <p className="font-medium whitespace-pre-line">
                                    {student.transportationNeeds}
                                  </p>
                                </div>
                              )}
                              {student.dietaryRestrictions && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Dietary restrictions
                                  </span>
                                  <p className="font-medium whitespace-pre-line">
                                    {student.dietaryRestrictions}
                                  </p>
                                </div>
                              )}
                            </div>
                          </section>
                        )}

                        {student.emergencyContact &&
                          typeof student.emergencyContact === 'object' && (
                            <section className="pt-6 border-t border-border">
                              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                                <Icon name="Phone" size={18} />
                                Emergency contact
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                {student.emergencyContact.name && (
                                  <div>
                                    <span className="text-muted-foreground">Name</span>
                                    <p className="font-medium">
                                      {student.emergencyContact.name}
                                    </p>
                                  </div>
                                )}
                                {student.emergencyContact.relationship && (
                                  <div>
                                    <span className="text-muted-foreground">Relationship</span>
                                    <p className="font-medium">
                                      {student.emergencyContact.relationship}
                                    </p>
                                  </div>
                                )}
                                {student.emergencyContact.phone && (
                                  <div>
                                    <span className="text-muted-foreground">Phone</span>
                                    <p className="font-medium">
                                      {student.emergencyContact.phone}
                                    </p>
                                  </div>
                                )}
                                {student.emergencyContact.email && (
                                  <div>
                                    <span className="text-muted-foreground">Email</span>
                                    <p className="font-medium">
                                      {student.emergencyContact.email}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </section>
                          )}

                        {student.notes && (
                          <section className="pt-6 border-t border-border">
                            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                              <Icon name="StickyNote" size={18} />
                              Notes
                            </h3>
                            <p className="text-sm text-foreground whitespace-pre-line">
                              {student.notes}
                            </p>
                          </section>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Team on this student — replaces the free-text
                      "Service Providers" list with real teammates from
                      the workspace. */}
                  {!isEditing && (
                    <StudentTeamPanel studentId={student.id} />
                  )}
                </div>
              )}

              {activeTab === 'behavior-support-plan' && (
                <BehaviorSupportPlan
                  studentId={student.id}
                  studentName={displayName}
                />
              )}

              {activeTab === 'iep-goals' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="text-center py-12">
                    <Icon
                      name="Target"
                      size={40}
                      className="text-muted-foreground mx-auto mb-3"
                    />
                    <h3 className="text-base font-medium text-foreground mb-1">
                      IEP goals for {displayName}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                      Track annual goals, benchmarks, and progress data for this
                      student. Open the full IEP Goals workspace to add or
                      update goals.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/iep-goals-and-objectives-tracking')}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      Open IEP Goals workspace
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'behavioral' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="text-center py-12">
                    <Icon
                      name="BarChart3"
                      size={40}
                      className="text-muted-foreground mx-auto mb-3"
                    />
                    <h3 className="text-base font-medium text-foreground mb-1">
                      Behavioral data for {displayName}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                      {studentIncidents.length === 0
                        ? 'No incidents logged for this student yet. Log incidents from the Behavioral Incidents workspace.'
                        : `${studentIncidents.length} incident${studentIncidents.length === 1 ? '' : 's'} logged. Open the workspace to view ABC data and analytics.`}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/behavioral-incident-logging')}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      Open Behavioral Incidents workspace
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <DocumentsList
                  documents={[]}
                  onDocumentUpload={handleDocumentUpload}
                  onDocumentDelete={handleDocumentDelete}
                />
              )}
            </div>

            {/* Right sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <StudentPhotoUpload
                student={student}
                onPhotoUpdate={handlePhotoUpdate}
              />

              <QuickStatsCard student={enrichedStudent} />

              <RecentActivityTimeline activities={[]} />

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-card-foreground mb-4">Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/iep-goals-and-objectives-tracking')}
                    iconName="Target"
                    iconPosition="left"
                    className="w-full justify-start"
                  >
                    View IEP goals
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/behavioral-incident-logging')}
                    iconName="AlertTriangle"
                    iconPosition="left"
                    className="w-full justify-start"
                  >
                    Log incident
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/reports-and-analytics')}
                    iconName="FileText"
                    iconPosition="left"
                    className="w-full justify-start"
                  >
                    Generate report
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleArchiveStudent}
                    iconName={isArchived ? 'ArchiveRestore' : 'Archive'}
                    iconPosition="left"
                    className="w-full justify-start"
                  >
                    {isArchived ? 'Restore to active' : 'Archive student'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteStudent}
                    iconName="Trash2"
                    iconPosition="left"
                    className="w-full justify-start text-destructive hover:text-destructive"
                  >
                    Delete permanently
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentProfileView;
